/* global window, document */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, message } from 'antd';
import { connect } from 'react-redux';
import CalendarSettingsView from '../../Components/CalendarSettings/CalendarSettingsComponent';
import { getSettings, getUrls, getSettingsLoadFailed } from '../../Reducers/SettingsReducer';
import * as settingsActions from '../../Actions/SettingsActions';
import { browserZone, zoneLabel, isSupportedZone } from '../../Utility/zonedTime';

const mapStateToProps = state => ({
  settings: getSettings(state),
  urls: getUrls(state),
  settingsLoadFailed: getSettingsLoadFailed(state),
});

const mapDispatchToProps = {
  fetchUrls: settingsActions.fetchUrls,
  fetchSettings: settingsActions.fetchSettings,
  setSettings: settingsActions.setSettings,
  revokeAccess: settingsActions.revokeAccess,
};

function zoneRequired(settings, adoptedZone) {
  if (adoptedZone && isSupportedZone(adoptedZone)) return false;
  const wanted = settings.TimeZone || browserZone();
  return !(settings.TimeZoneIds || []).some(id => id.toLowerCase() === (wanted || '').toLowerCase() && isSupportedZone(id));
}

class CalendarSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      adoptedZone: null,
      saving: false,
      saveError: '',
      zoneConflict: false,
      saveStatus: '',
    };
    this.showModal = this.showModal.bind(this);
    this.handleOk = this.handleOk.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.openProvider = this.openProvider.bind(this);
    this.adoptZone = this.adoptZone.bind(this);
    this.chooseZone = this.chooseZone.bind(this);
  }

  componentDidMount() {
    const { openZoneRequest, focusZoneRequest } = this.props;
    if (openZoneRequest) this.showModal(focusZoneRequest === null || focusZoneRequest > 0);
  }

  componentDidUpdate(prevProps) {
    const { openZoneRequest, focusZoneRequest } = this.props;
    if (openZoneRequest !== prevProps.openZoneRequest && openZoneRequest) {
      this.showModal(focusZoneRequest === null || focusZoneRequest !== prevProps.focusZoneRequest);
    }
    if (this.focusZonePending) this.chooseZone();
  }

  showModal = focusZone => {
    const { visible } = this.state;
    const { fetchUrls, fetchSettings } = this.props;
    this.focusZonePending = focusZone === true;
    this.openTrigger = document.activeElement;
    this.setState({ visible: true });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && visible) {
        fetchUrls();
      }
    });
    fetchUrls();
    if (fetchSettings) fetchSettings();
  };

  openProvider = providerName => {
    const { urls } = this.props;
    if (urls[providerName] && urls[providerName].Url) {
      if (!urls[providerName].Registered) {
        window.open(urls[providerName].Url, '_blank');
      }
    }
  };

  handleOk = e => {
    e.preventDefault();
    const { saving, adoptedZone } = this.state;
    if (saving) return;
    const { form, setSettings, settings: storedSettings } = this.props;
    form.validateFields((err, values) => {
      if (!err) {
        const zoneIds = storedSettings.TimeZoneIds || [];
        const storedZone = zoneIds.find(id => id.toLowerCase() === (storedSettings.TimeZone || '').toLowerCase());
        const suggestedZone = !storedSettings.TimeZone && zoneIds.find(id => id.toLowerCase() === (browserZone() || '').toLowerCase());
        const zone = adoptedZone || storedZone || (!storedSettings.TimeZone && values.timeZone) || suggestedZone;
        if (!zone || !zoneIds.includes(zone) || !isSupportedZone(zone)) {
          this.setState({ saveError: 'Choose your time zone' });
          return;
        }
        if (values.workingHoursStartTime.format('HH:mm') === values.workingHoursEndTime.format('HH:mm')) {
          this.setState({ saveError: "Start and end can't be the same time." });
          return;
        }
        const settings = {
          workingHoursStart: values.workingHoursStartTime.format('HH:mm:ss'),
          workingHoursEnd: values.workingHoursEndTime.format('HH:mm:ss'),
          availableWeekDays: values.days,
          SlotDuration: values.defaultTimeSlot,
          DefaultReminderTime: values.defaultReminderTime,
          BufferDuration: values.bufferDuration,
          TimeZoneIds: storedSettings.TimeZoneIds,
          TimeZone: zone,
        };
        this.setState({ saving: true, saveError: '', zoneConflict: false });
        setSettings(settings).then(() => {
          this.setState({ visible: false, adoptedZone: null, saving: false, saveStatus: `Working hours saved in ${zoneLabel(zone).split(' – ')[0]}` }, () => {
            if (this.trigger || this.openTrigger) (this.trigger || this.openTrigger).focus();
          });
          message.success(`Working hours saved in ${zoneLabel(zone).split(' – ')[0]}`);
        }).catch(error => {
          const conflict = error.response && error.response.status === 409 && error.response.data && error.response.data.code === 'zone_set_update_app';
          this.setState({ saving: false, saveError: conflict ? error.response.data.message : "We couldn't save working hours. Please try again.", zoneConflict: conflict });
        });
      }
    });
  };

  handleCancel = () => {
    const { saving, visible } = this.state;
    if (saving) return;
    const { form, fetchUrls } = this.props;
    this.setState({
      visible: false,
      adoptedZone: null,
      saveError: '',
      zoneConflict: false,
    });
    form.resetFields();
    if (this.trigger || this.openTrigger) (this.trigger || this.openTrigger).focus();
    document.removeEventListener('visibilitychange', () => {
      if (!document.hidden && visible) {
        fetchUrls();
      }
    });
  };

  adoptZone(zone) {
    const { form } = this.props;
    this.setState({ adoptedZone: zone });
    form.setFieldsValue({ timeZone: zone });
  }

  chooseZone() {
    if (this.zoneSelect) {
      this.zoneSelect.focus();
      this.focusZonePending = false;
    }
  }

  render() {
    const { visible, adoptedZone, saving, saveError, zoneConflict, saveStatus } = this.state;
    const { form, settings, urls, refreshCalendar, revokeAccess, fetchUrls, fetchSettings, settingsLoadFailed, showTrigger } = this.props;
    return (
      <CalendarSettingsView
        visible={visible}
        showTrigger={showTrigger}
        handleOk={this.handleOk}
        handleCancel={this.handleCancel}
        showModal={this.showModal}
        form={form}
        openProvider={this.openProvider}
        userSettings={settings}
        externalProviders={urls}
        refreshCalendar={refreshCalendar}
        revokeAccess={revokeAccess}
        fetchUrls={fetchUrls}
        fetchSettings={fetchSettings}
        settingsLoadFailed={settingsLoadFailed}
        adoptedZone={adoptedZone}
        saving={saving}
        saveError={saveError}
        saveStatus={saveStatus}
        zoneConflict={zoneConflict}
        zoneRequired={zoneRequired(settings, adoptedZone)}
        onAdoptZone={this.adoptZone}
        onChooseZone={this.chooseZone}
        zoneSelectRef={select => { this.zoneSelect = select; if (select && this.focusZonePending) this.chooseZone(); }}
        triggerRef={trigger => { this.trigger = trigger && trigger.querySelector('button'); }}
      />
    );
  }
}

CalendarSettings.propTypes = {
  openZoneRequest: PropTypes.number,
  focusZoneRequest: PropTypes.number,
  showTrigger: PropTypes.bool,
  form: PropTypes.shape({
    validateFields: PropTypes.func,
    getFieldDecorator: PropTypes.func,
    resetFields: PropTypes.func,
    setFieldsValue: PropTypes.func,
  }).isRequired,
  fetchUrls: PropTypes.func.isRequired,
  fetchSettings: PropTypes.func,
  setSettings: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    freeStartTime: PropTypes.string,
    freeEndTime: PropTypes.string,
    weekAvailabilityList: PropTypes.arrayOf(PropTypes.string),
    TimeZone: PropTypes.string,
    TimeZoneIds: PropTypes.arrayOf(PropTypes.string),
    LocalStartingFreeTime: PropTypes.string,
    LocalEndingFreeTime: PropTypes.string,
  }),
  urls: PropTypes.shape({}),
  revokeAccess: PropTypes.func.isRequired,
  refreshCalendar: PropTypes.func.isRequired,
};

CalendarSettings.defaultProps = {
  openZoneRequest: 0,
  focusZoneRequest: null,
  showTrigger: true,
  settings: {},
  urls: {},
  fetchSettings: () => {},
};

const CalendarSettingsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(Form.create()(CalendarSettings));

export { CalendarSettings };
export default CalendarSettingsContainer;
